import React from "react";

import AccordionWrapper from "react-faq-component";

import tatariLogo from "@assets/tatari-logo.svg";
import { useUser } from "@components/user-context";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { useNorthbeamQuery } from "@hooks/http/use-northbeam-query";
import { Loader } from "@mantine/core";
import {
  ConnectionType,
  type FetchOrderPlatformAndEcommerceOptions,
} from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  type LoginComponentChildrenProps,
  type LoginComponentProps,
} from "@pages/settings/connection-status-manager";
import { PrimaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import TextBoxWithCopyButton from "@shared/text-box-with-copy-button";

import { FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS } from "../queries";

const type = ConnectionType.integration_tatari;
const ORDERS_SOURCES = [
  { value: "shopify", label: "Shopify" } as const,
  { value: "custom", label: "Orders API" } as const,
];

type OrdersSource = (typeof ORDERS_SOURCES)[number]["value"];

export function TatariConnectionCard() {
  const { user } = useUser();
  const successfulConnectionExtraText = React.useCallback(
    () => (
      <AccordionWrapper
        data={{
          rows: [
            {
              title: "Configure the AWS Data Sharing to activate integration",
              content: (
                <>
                  <p className="card-text">
                    Configure the <b>AWS Data Sharing</b> Integration in your
                    Tatari account with the data below:
                    <LearnMoreTooltip
                      title="AWS Data Sharing Configuration"
                      content="You need to setup AWS Data Sharing in your Tatari account"
                      link="https://docs.northbeam.io/docs/tracking-for-tatari#step-4-insert-aws-credentials"
                    />
                  </p>
                  <p className="card-text space-y-2">
                    <TextBoxWithCopyButton
                      label="AWS Account ID"
                      text="892581138023"
                      textCopiedNotificationMessage="AWS Account ID copied!"
                    />
                    <TextBoxWithCopyButton label="Type" text="User" />
                    <TextBoxWithCopyButton
                      label="Name"
                      text={`nb-client-gen__${user.displayUserId}__tatari`}
                      textCopiedNotificationMessage="Name copied!"
                    />
                  </p>
                </>
              ),
            },
          ],
        }}
        styles={{
          bgColor: "white",
          rowContentTextSize: "16px",
          rowContentPaddingTop: "10px",
          rowContentPaddingBottom: "10px",
          rowContentPaddingRight: "20px",
          arrowColor: "black",
        }}
        config={{ tabFocus: true }}
      />
    ),
    [user.displayUserId],
  );

  return (
    <ConnectionCard
      type={type}
      connectionTypeName="Tatari"
      connectionCallToAction={`
      Integrate your Tatari account
      `}
      logoUrl={tatariLogo}
      logoAltText="Tatari Logo"
      successfulConnectionExtraText={successfulConnectionExtraText}
      knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-tatari"
    >
      {({
        upsertConnection,
        isLoading,
        accounts,
      }: LoginComponentChildrenProps) => (
        <TatariLoginComponent
          upsertConnection={upsertConnection}
          isLoading={isLoading}
          accounts={accounts}
        />
      )}
    </ConnectionCard>
  );
}

function TatariLoginComponent({
  upsertConnection,
  isLoading,
}: Omit<LoginComponentProps, "setStatus" | "integrationDetails">) {
  const [tatariId, setTatariId] = React.useState("");
  const [ordersSource, setOrdersSource] = React.useState<
    OrdersSource | undefined
  >();

  const { loading: isLoadingOrderPlatformAndECommerceOptions } =
    useNorthbeamQuery<FetchOrderPlatformAndEcommerceOptions>(
      FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS,
      {
        doNotRaiseErrorOnFailure: true,
        onCompleted: (data) => {
          const hasServerToServerPlatform =
            data.me.ecommercePlatforms?.includes("Server to Server");
          const initialOption = hasServerToServerPlatform
            ? "custom"
            : "shopify";
          setOrdersSource(initialOption);
        },
      },
    );

  const onConnectClicked = React.useCallback(async () => {
    await upsertConnection({
      tatari_slug: tatariId.trim(),
      tatari_user_id_type: ordersSource?.trim(),
    });
  }, [ordersSource, tatariId, upsertConnection]);

  return (
    <div className="mb-3">
      <div className="flex flex-col gap-y-5 mt-5">
        <div>
          <div>
            Tatari ID{" "}
            <LearnMoreTooltip
              title="Tatari ID"
              content="Check here how to find your Tatari ID"
              link="https://docs.northbeam.io/docs/tracking-for-tatari#step-2-share-your-tatari-id-with-northbeam"
            />
          </div>
          <input
            disabled={isLoading}
            type="text"
            className="form-control max-w-xl"
            value={tatariId}
            placeholder="Tatari ID..."
            id="tatariId"
            onChange={(event) => setTatariId(event.target.value)}
          />
        </div>
        <div>
          <div>
            Orders source of truth{" "}
            <LearnMoreTooltip
              title="Orders source of truth"
              content={
                <>
                  The source of truth for your orders, which will be used to
                  match with your Tatari data: <br />
                  It could be either <i>Shopify</i> or our <i>Orders API</i>.
                </>
              }
            />
          </div>
          <Select
            icon={
              isLoadingOrderPlatformAndECommerceOptions && (
                <Loader color="blue" size={12} />
              )
            }
            value={ordersSource}
            options={ORDERS_SOURCES}
            onChange={setOrdersSource}
            className="max-w-xl"
            isDisabled={isLoadingOrderPlatformAndECommerceOptions}
          />
        </div>
        <div>
          <PrimaryButton
            loading={isLoading}
            disabled={!tatariId.trim() || !ordersSource?.trim()}
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
